import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { Rangoli, Diya, Potli, Rakhi, Lantern, Hanging, Marigold } from "./motifs";
import { wa } from "@/lib/site";

const COLLECTIONS = [
    {
        name: "Rangoli Mats",
        desc: "Colourful, reusable decorative floor mats for Diwali and festive styling.",
        Motif: Rangoli,
        accent: "#EA580C",
        span: "lg:col-span-2",
    },
    {
        name: "Pooja & Mandir Essentials",
        desc: "Bandhani-style aasan mats in two sizes and three colourways, plus decorative bajot/chowki platforms with fabric, gold detailing and mirror work.",
        Motif: Diya,
        accent: "#BE123C",
        span: "lg:row-span-2",
    },
    {
        name: "Festive Potli Bags",
        desc: "Ornate Bandhani-print gifting pouches with brocade panels, gold embroidery and pearl handles.",
        Motif: Potli,
        accent: "#047857",
        span: "",
    },
    {
        name: "Raksha Bandhan",
        desc: "Handpicked, premium rakhi merchandise for the sibling bond.",
        Motif: Rakhi,
        accent: "#BE123C",
        span: "",
    },
    {
        name: "Diya Holders",
        desc: "Festive lighting pieces for Diwali and warm everyday home décor.",
        Motif: Lantern,
        accent: "#E2BA03",
        span: "lg:col-span-2",
    },
    {
        name: "Festive Hangings",
        desc: "Decorative hangings for doorways, walls and festive home styling.",
        Motif: Hanging,
        accent: "#047857",
        span: "",
    },
];

const CollectionCard = ({ item, index }) => {
    const { name, desc, Motif, accent, span } = item;
    return (
        <motion.a
            href={wa(`Hi Festive Theory! I'd love to enquire about your ${name}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`collection-card-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            initial={{ y: 48, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.85, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true, margin: "-60px" }}
            className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-[#472500]/15 bg-[#F7F4EC] p-8 sm:p-10 min-h-[280px] transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-2 hover:border-[#472500]/35 hover:shadow-[0_30px_60px_-30px_rgba(71,37,0,0.35)] ${span}`}
        >
            <Motif
                className="absolute -right-8 -bottom-8 w-44 h-44 sm:w-56 sm:h-56 opacity-[0.14] transition-transform duration-700 ease-out group-hover:scale-110 group-hover:rotate-6"
                style={{ color: accent }}
            />
            <div className="relative z-10">
                <span
                    className="inline-block h-1.5 w-10 rounded-full mb-6 transition-[width] duration-500 group-hover:w-16"
                    style={{ backgroundColor: accent }}
                />
                <h3 className="font-display text-2xl sm:text-3xl text-[#472500]">{name}</h3>
                <p className="mt-3 text-sm sm:text-base text-[#472500]/70 leading-relaxed max-w-md">{desc}</p>
            </div>
            <div className="relative z-10 mt-8 flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.25em] text-[#472500]/50">Enquire for price</span>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#472500]">
                    Enquire on WhatsApp
                    <span className="grid place-items-center w-8 h-8 rounded-full border border-[#472500]/30 transition-all duration-300 group-hover:bg-[#E2BA03] group-hover:border-[#E2BA03]">
                        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:rotate-45" />
                    </span>
                </span>
            </div>
        </motion.a>
    );
};

const Collections = () => (
    <section id="collections" data-testid="collections-section" className="relative py-28 sm:py-40">
        <Marigold
            className="absolute top-16 right-[6%] w-24 h-24 text-[#EA580C] opacity-20 animate-spin-slower hidden lg:block"
            aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-16 sm:mb-20">
                <SectionHeading
                    eyebrow="Shop by Collection"
                    title={<>Our <em className="italic text-[#E2BA03] [text-shadow:0_1px_0_#472500]">Collections</em></>}
                    testId="collections-heading"
                />
                <Reveal delay={0.15} className="max-w-sm">
                    <p className="text-sm sm:text-base text-[#472500]/65 leading-relaxed">
                        Every piece is handpicked and richly detailed — message us on WhatsApp to check
                        designs, availability and pricing.
                    </p>
                </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {COLLECTIONS.map((c, i) => (
                    <CollectionCard key={c.name} item={c} index={i} />
                ))}
            </div>
        </div>
    </section>
);

export default Collections;
